/**
 * Merchant app shell.
 *
 * Provider order matters:
 *   Appearance  outermost, so the `dark` class is on the document root before
 *               anything below it resolves a colour.
 *   Locale      next, so every message below is translated.
 *   Clock       one timer for every countdown in the app.
 *   Toast       renders above every screen, so it sits outside the router.
 *   Merchant    session and restaurant; gates everything behind it.
 *
 * Density is not a provider: the rail and the tab bar are chosen from Tailwind
 * breakpoints through `useResponsive()`, so a viewport that crosses the tablet
 * width re-lays out without a second theme resolution.
 *
 * The dashboard is designed for the iPad first, but a merchant checking takings
 * on their phone must not be handed a broken layout.
 *
 * Access is gated in three steps: a real account, then at least one restaurant,
 * then the dashboard. Every step is enforced before the next is mounted, so no
 * screen ever renders against a missing restaurant.
 */

import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  AppearanceProvider,
  ClockProvider,
  LocaleProvider,
  PortalHost,
  ToastProvider,
  useT,
} from '@yummy/ui-rnr';

import { MerchantProvider, useMerchant } from './app/auth';
import { OrdersProvider } from './app/orders';
import { FullScreenMessage } from './components/FullScreenMessage';
import { MerchantShell } from './components/MerchantShell';
import { AuthScreen } from './screens/AuthScreen';
import { PickupScreen } from './screens/PickupScreen';
import { CapacityScreen } from './screens/CapacityScreen';
import { CategoryFormScreen } from './screens/CategoryFormScreen';
import { CookScreen } from './screens/CookScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { ItemFormScreen } from './screens/ItemFormScreen';
import { StockFormScreen } from './screens/StockFormScreen';
import { HoursScreen } from './screens/HoursScreen';
import { MenuScreen } from './screens/MenuScreen';
import { MenuScanScreen } from './screens/MenuScanScreen';
import { OnboardingScreen } from './screens/OnboardingScreen';
import { OrdersScreen } from './screens/OrdersScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { TableFormScreen } from './screens/TableFormScreen';
import { TablesScreen } from './screens/TablesScreen';
import { CircleHelp, LoaderCircle } from 'lucide-react-native';


/**
 * Chooses sign-in or sign-up from the URL.
 *
 * @returns The auth screen for the current path.
 */
function AuthGate() {
  const location = useLocation();
  const mode = location.pathname.startsWith('/signup') ? 'signup' : 'login';
  return <AuthScreen mode={mode} />;
}

/**
 * Everything behind authentication.
 *
 * @returns The dashboard, or whichever earlier access step is still unmet.
 */
function Dashboard() {
  const { session, ready, restaurants } = useMerchant();
  const location = useLocation();
  const t = useT();

  if (!ready) {
    return (
      <FullScreenMessage
        icon={LoaderCircle}
        title={t('merchant.auth.loadingTitle')}
        message={t('merchant.auth.loading')}
      />
    );
  }

  if (!session) return <AuthGate />;

  // A signed-in account with no restaurant is mid-onboarding.
  if (restaurants.length === 0) {
    return <OnboardingScreen />;
  }

  // Sign-up and sign-in are modes of one screen, chosen by the URL, not routes.
  // Nothing moves the URL once they succeed, so the shell rendered at /signup
  // matched no route and showed "Page not found" right after a successful
  // sign-up. Onboarding finishing lands here too, by the same path.
  if (location.pathname === '/signup' || location.pathname === '/login') {
    return <Navigate to="/" replace />;
  }

  return (
    <OrdersProvider>
      <MerchantShell>
        <Routes>
          <Route path="/" element={<DashboardScreen />} />
          <Route path="/orders" element={<OrdersScreen />} />
          {/* The kitchen's list, which used to be a panel beside the queue. It
              is a page of its own so neither screen has to share its width. */}
          <Route path="/cook" element={<CookScreen />} />
          {/* What a customer's pickup code opens. Reachable by camera; the
              session gate above runs first, and leaves this URL alone. */}
          <Route path="/pickup/:token" element={<PickupScreen />} />
          <Route path="/menu" element={<MenuScreen />} />
          <Route path="/menu/scan" element={<MenuScanScreen />} />
          {/* Forms are pages, not dialogs: a dialog renders through a portal,
              outside the screen that knows where the keyboard is. */}
          <Route path="/menu/categories/new" element={<CategoryFormScreen />} />
          <Route path="/menu/categories/:categoryId" element={<CategoryFormScreen />} />
          <Route path="/menu/items/new" element={<ItemFormScreen />} />
          <Route path="/menu/items/:itemId" element={<ItemFormScreen />} />
          <Route path="/menu/items/:itemId/stock" element={<StockFormScreen />} />
          <Route path="/hours" element={<HoursScreen />} />
          {/* The kitchen measured against its own queue. A page rather than a
              card in Settings: the knobs are set there, but reading them against
              a model is a different job. */}
          <Route path="/capacity" element={<CapacityScreen />} />
          <Route path="/tables" element={<TablesScreen />} />
          {/* A form, not a dialog: a dialog renders through a portal, outside
              the screen that knows about the on-screen keyboard. */}
          <Route path="/tables/new" element={<TableFormScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route
            path="*"
            element={
              <FullScreenMessage
                icon={CircleHelp}
                title={t('common.notFound')}
                message={t('common.somethingWentWrong')}
                actionLabel={t('merchant.nav.dashboard')}
                onAction={() => {
                  window.location.href = '/';
                }}
              />
            }
          />
        </Routes>
      </MerchantShell>
    </OrdersProvider>
  );
}

/**
 * The merchant application.
 *
 * @returns The provider stack around the routed dashboard.
 */
export function App() {
  return (
    <AppearanceProvider storageKey="yummy.merchant.appearance">
      <LocaleProvider storageKey="yummy.merchant.locale">
        <ClockProvider>
          <ToastProvider>
            <MerchantProvider>
              <BrowserRouter>
                <Dashboard />
              </BrowserRouter>
              {/* Mounted once: every overlay component renders through it, so a
                  dialog is never clipped by the screen that opened it. */}
              <PortalHost />
            </MerchantProvider>
          </ToastProvider>
        </ClockProvider>
      </LocaleProvider>
    </AppearanceProvider>
  );
}
